import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { usePanelContext } from "./context";

export const PanelResizeHandle = () => {
  const {
    side = "right",
    minWidth = 300,
    maxWidth,
    handleResize,
  } = usePanelContext();
  const { rtl } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const effectiveMaxWidth = maxWidth ?? window.innerWidth * 0.8;

  useEffect(() => {
    if (!isDragging) return;

    let lastCall = 0;
    const throttleMs = 16;

    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastCall < throttleMs) return;
      lastCall = now;

      let width: number;

      if (side === "right") {
        width = rtl ? e.clientX : window.innerWidth - e.clientX;
      } else {
        width = rtl ? window.innerWidth - e.clientX : e.clientX;
      }

      const clampedWidth = Math.max(
        minWidth,
        Math.min(effectiveMaxWidth, width),
      );
      handleResize(clampedWidth);
    };

    const handleMouseUp = () => setIsDragging(false);

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, side, rtl, minWidth, effectiveMaxWidth, handleResize]);

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  return (
    <div
      ref={ref}
      onMouseDown={handleMouseDown}
      className={cn(
        "absolute top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary-500/50 transition-colors z-[1]",
        side === "right" ? "start-0" : "end-0",
        isDragging && "bg-primary-500",
        "select-none",
      )}
    />
  );
};
